import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Popover } from 'antd';
import { useIntl } from 'react-intl';
import { TaskButtons } from '../buttons/TaskButtons';
import {
  checkContentForUploadingImage,
  prepareContentForSending,
} from '../../molecules/editor/utils/editorContentOperations';
import TextEditorWrapper from '../../molecules/editor/TextEditorWrapper';
import HtmlRenderer from '../../organisms/HtmlRenderer';

const popoverContentStyles = {
  border: '1px solid #D6D6D6',
  padding: '0.75rem 1rem',
  borderRadius: '10px',
};

const renderedValueContainerStyles = {
  padding: '8px 12px',
  minHeight: 40,
};

const emptyMentionOptions = [];

const getPopupContainer = () => document.body;

const RichTextCustomField = ({ field, onChange }) => {
  const intl = useIntl();
  const textEditorRef = useRef({});

  const value = useMemo(
    () =>
      typeof field?.value === 'string'
        ? field.value
        : String(field?.value || ''), //prevent error when field type changes
    [field?.value],
  );

  const [renderedValue, setRenderedValue] = useState(value);
  const [updatedValue, setUpdatedValue] = useState(value);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setRenderedValue(value);
    setUpdatedValue(value);

    if (textEditorRef.current?.commands?.setContent) {
      textEditorRef.current.commands.setContent(value || '');
    }
  }, [value]);

  const handleSave = useCallback(() => {
    textEditorRef?.current?.commands?.submit();
  }, []);

  const cancelHandler = useCallback(() => {
    setUpdatedValue(value);
    if (textEditorRef.current?.commands?.setContent) {
      textEditorRef.current.commands.setContent(value || '');
    }
    setOpen(false);
  }, [value]);

  const handleEditorSubmit = useCallback(
    (content) => {
      const prepared = prepareContentForSending(content);
      onChange(prepared);
      setRenderedValue(prepared);
      setUpdatedValue(prepared);
      setOpen(false);
    },
    [onChange],
  );

  const isSendDisabled = useCallback(() => {
    const prepared = updatedValue && prepareContentForSending(updatedValue);
    return field?.value === prepared || checkContentForUploadingImage(updatedValue);
  }, [updatedValue, field?.value]);

  const content = useMemo(
    () => (
      <div className="w-[600px]">
        <div style={popoverContentStyles}>
          <TextEditorWrapper
            placeholder={intl.formatMessage({
              id: 'ENTER_DETAILS',
              defaultMessage: 'Enter details...',
            })}
            content={updatedValue}
            initialContent={value}
            onChange={setUpdatedValue}
            onSubmit={handleEditorSubmit}
            textEditorRef={textEditorRef}
            mentionOptions={emptyMentionOptions}
            showMentionOptions={false}
          />
        </div>
        <div className="flex justify-end mt-2">
          <TaskButtons
            className="justify-end"
            disabled={isSendDisabled()}
            onHandle={handleSave}
            onCancel={cancelHandler}
          />
        </div>
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cancelHandler,
      handleEditorSubmit,
      handleSave,
      isSendDisabled,
      updatedValue,
      value,
    ],
  );

  return (
    <div className="flex justify-start items-center h-full w-full">
      <Popover
        content={content}
        title={intl.formatMessage({
          id: 'ADD_TEXT',
          defaultMessage: 'Add text',
        })}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottom"
        overlayClassName="custom-field-popover"
        destroyTooltipOnHide={false}
        getPopupContainer={getPopupContainer}
      >
        {renderedValue?.length > 0 ? (
          <div
            className={`flex items-center w-full cursor-pointer rounded-r hover:bg-indigo-50/50 transition-colors ${
              open ? 'bg-indigo-50/40' : ''
            }`}
            style={renderedValueContainerStyles}
          >
            <span className="text-[13px] line-clamp-1 w-full" style={{ color: 'rgba(15,15,25,0.8)' }}>
              <HtmlRenderer html={renderedValue} />
            </span>
          </div>
        ) : (
          <div className="flex items-center px-3 w-full cursor-pointer rounded-r hover:bg-indigo-50/50 transition-colors" style={{ minHeight: 40 }}>
            <span className="text-[13px]" style={{ color: 'rgba(15,15,25,0.28)' }}>Empty</span>
          </div>
        )}
      </Popover>
    </div>
  );
};

export default RichTextCustomField;
